import { useState, useEffect } from 'react';
import axios from 'axios';
import { useBottomSheet } from '../contexts/BottomSheetContext';

const getApiUrl = () => {
  const RAILWAY_API = import.meta.env.VITE_API_URL || 'https://registration-app-production.up.railway.app';
  const LOCALHOST_API = 'http://localhost:3000';
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  return isLocalhost ? LOCALHOST_API : RAILWAY_API;
};

const CreateQuestionForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'general',
    tags: '',
    urgency_level: 'medium',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [miningStatus, setMiningStatus] = useState(null);
  const [categories, setCategories] = useState([]);
  const [urgencyLevels, setUrgencyLevels] = useState([]);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const { closeBottomSheet } = useBottomSheet();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const API_URL = getApiUrl();

        const categoryResponse = await axios.get(
          `${API_URL}/api/system-parameters/question-categories`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (categoryResponse.data.success) {
          setCategories(categoryResponse.data.data);
        }

        const urgencyResponse = await axios.get(
          `${API_URL}/api/system-parameters/options/urgency_levels`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (urgencyResponse.data.success) {
          setUrgencyLevels(urgencyResponse.data.data);
        }
      } catch (error) {
        setCategories([
          { value: 'general', label: '一般' },
          { value: 'community', label: 'コミュニティ' },
          { value: 'technology', label: '技術' },
          { value: 'environment', label: '環境' },
          { value: 'economy', label: '経済' },
          { value: 'governance', label: 'ガバナンス' },
          { value: 'social', label: '社会' },
          { value: 'education', label: '教育' },
        ]);
        setUrgencyLevels([
          { value: 'low', label: '🟢 低 - 時間をかけて検討' },
          { value: 'medium', label: '🟡 中 - 適度な時間で対応' },
          { value: 'high', label: '🔴 高 - 早急な対応が必要' },
        ]);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMiningStatus(null);

    try {
      const token = localStorage.getItem('token');
      const API_URL = getApiUrl();

      const tagsArray = formData.tags
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);

      const questionData = {
        title: formData.title,
        content: formData.content,
        urgency_level: formData.urgency_level,
        type: 'question',
        metadata: {
          category: formData.category,
          tags: tagsArray,
        },
      };

      setMiningStatus('mining');

      await axios.post(
        `${API_URL}/api/consensus/questions`,
        questionData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMiningStatus('success');
      setTimeout(() => {
        closeBottomSheet();
        if (onSuccess) onSuccess();
      }, 1500);
    } catch (err) {
      setMiningStatus('error');
      setError(err.response?.data?.message || '問いの作成に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* 説明文 */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 p-3 rounded-lg border border-green-200">
        <p className="text-gray-700 text-sm leading-relaxed">
          あなたの違和感や気づきを種として森に植えてください。あなたの植えたその種は意味の発火点となり、森に新たな息吹を与えてくれるでしょう。
        </p>
      </div>

      {/* 心得（折りたたみ可能） */}
      <button
        type="button"
        onClick={() => setShowGuidelines(!showGuidelines)}
        className="w-full px-3 py-2 text-left flex items-center justify-between hover:bg-green-50 transition-colors rounded-lg border border-green-200"
      >
        <h3 className="text-sm text-green-900">💡 森に種を植えるための心得</h3>
        <svg
          className={`w-4 h-4 text-green-700 transform transition-transform ${showGuidelines ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {showGuidelines && (
        <div className="px-3 py-2 bg-green-50 rounded-lg border border-green-200">
          <ul className="space-y-1 text-green-800 text-sm">
            <li>• <strong>🌰 質問ではなく問い</strong>：答えを求めるのではなく、共感する仲間を求める</li>
            <li>• <strong>🐛 違和感から始まる</strong>：あなたが感じた「これってどうなの？」という生の違和感</li>
            <li>• <strong>🌱 意味の発火点</strong>：その問いから芽吹く行動や変化を想像してみる</li>
            <li>• <strong>🦋 森の住人として</strong>：DAOの生命体として、あなたの存在を信頼とともに表明する</li>
          </ul>
        </div>
      )}

      {/* エラー表示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* マイニング状態表示 */}
      {miningStatus && (
        <div className={`p-4 rounded-lg border ${
          miningStatus === 'mining' ? 'bg-blue-50 border-blue-200' :
          miningStatus === 'success' ? 'bg-green-50 border-green-200' :
          'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-center gap-3">
            {miningStatus === 'mining' && (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                <p className="text-blue-700 font-medium">🌱 エコサイクル・マイニング中...</p>
              </>
            )}
            {miningStatus === 'success' && (
              <>
                <div className="text-green-500 text-xl">✅</div>
                <p className="text-green-700 font-medium">🎉 エコサイクル・マイニング完了！SHINKETSUトークンを獲得しました</p>
              </>
            )}
            {miningStatus === 'error' && (
              <>
                <div className="text-red-500 text-xl">❌</div>
                <p className="text-red-700 font-medium">💧 エコサイクル・マイニングに失敗しました</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* フォーム */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* タイトル */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            問いのタイトル *（種の名前）
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="例: なぜ空気エンジンは量産されてないの？"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            required
            disabled={loading}
          />
        </div>

        {/* カテゴリ */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            カテゴリ
          </label>
          <select
            name="category"
            value={formData.category}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            disabled={loading}
          >
            {categories.map((cat) => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>

        {/* 緊急度 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            緊急度
          </label>
          <select
            name="urgency_level"
            value={formData.urgency_level}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            disabled={loading}
          >
            {urgencyLevels.map((level) => (
              <option key={level.value} value={level.value}>{level.label}</option>
            ))}
          </select>
        </div>

        {/* 内容 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            問いの背景と意図 *（種に込めた想い）
          </label>
          <textarea
            name="content"
            value={formData.content}
            onChange={handleChange}
            rows={6}
            placeholder="あなたがこの問いを抱くようになった違和感や気づきを教えてください。"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
            required
            disabled={loading}
          />
        </div>

        {/* タグ */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            タグ（カンマ区切り）
          </label>
          <input
            type="text"
            name="tags"
            value={formData.tags}
            onChange={handleChange}
            placeholder="例: 豊かさ, 価値観, 幸福, コミュニティ"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            disabled={loading}
          />
        </div>

        {/* ボタン */}
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={closeBottomSheet}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={loading}
          >
            キャンセル
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? '投稿中...' : '🌱 問いを植える'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateQuestionForm;
